import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import type { AdvertiserDto } from '@/services/ws-selfbooking/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { formatSelfBookingAdvertiserOptionLabel } from '../helpers';
import { useAdvertiserSelfbookingOptions } from '../hooks/useAdvertiserSelfbookingOptions';

interface AdvertiserSelfbookingFilterProps {
  value: Option<AdvertiserDto> | null;
  onChange: (value: SingleValue<Option<AdvertiserDto>>) => void;
}

export const AdvertiserSelfbookingFilter: React.FC<AdvertiserSelfbookingFilterProps> = React.memo(function AdvertiserSelfbookingFilter({
  value,
  onChange,
}) {
  const [t, { language }] = useTranslation(['backend', 'common']);

  const { options, pending: loading, total, searchFilter, setSearchFilter } = useAdvertiserSelfbookingOptions();

  const handleChange = useCallbackRef((newValue: SingleValue<Option<AdvertiserDto>>) => {
    onChange(newValue);
  });

  const isOptionSelected = useCallbackRef((a: Option<AdvertiserDto>, b: Option<AdvertiserDto>) => a.value.id === b.value.id);

  return (
    <Filter<AdvertiserDto>
      label={t('common:ADVERTISER')}
      loading={loading}
      selected={value}
      onChange={handleChange}
      serverSideFiltering={{
        filter: searchFilter,
        setFilter: setSearchFilter,
        total,
      }}
      options={options}
      formatOptionLabel={(option) => formatSelfBookingAdvertiserOptionLabel(option.value, language)}
      optionSelected={isOptionSelected}
      dataTestId='account-dropdown'
    />
  );
});
